import React, { useState } from 'react';
import { CircleDot, CheckCircle2 } from 'lucide-react';

const PricingPlans = () => {
    // State to manage the active currency tab
    const [currency, setCurrency] = useState('₹'); // Default to Indian Rupees

    // Define pricing plans for both currencies
    const plans = {
        '₹': [
            { dialogs: '100', price: '990' },
            { dialogs: '500', price: '2890' },
            { dialogs: '2k', price: '3650' },
            { dialogs: '5k', price: '5860' },
            { dialogs: '10k', price: '9260' },
            { dialogs: '15k', price: '12240' },
            { dialogs: '20k', price: '14790' },
            { dialogs: '30k', price: '19890' },
            { dialogs: '40k', price: '24140' },
            { dialogs: '50k', price: '27540' },
            { dialogs: '70k', price: '34760' },
            { dialogs: '100k', price: '44540' },
        ],
        '$': [ // Example dollar prices - adjust as needed for actual conversion
            { dialogs: '100', price: '12' },
            { dialogs: '500', price: '35' },
            { dialogs: '2k', price: '45' },
            { dialogs: '5k', price: '70' },
            { dialogs: '10k', price: '110' },
            { dialogs: '15k', price: '145' },
            { dialogs: '20k', price: '175' },
            { dialogs: '30k', price: '240' },
            { dialogs: '40k', price: '290' },
            { dialogs: '50k', price: '330' },
            { dialogs: '70k', price: '415' },
            { dialogs: '100k', price: '530' },
        ],
    };

    // Features included in all plans
    const includedFeatures = [
        'Unlimited chatbots',
        'Unlimited integrations',
        'Built-in ChatGPT integration',
        'Access to REST API',
        '1 WhatsApp account',
    ];

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 sm:p-8 font-sans">
            <div className="w-full max-w-7xl">
                {/* --- Header Section --- */}
                <div className="text-center mb-12 sm:mb-16">
                    <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 leading-tight">
                        Plans for <span className="text-cyan-500">any</span> budget
                    </h1>
                </div>

                {/* --- Currency Toggle Buttons --- */}
                <div className="flex justify-center mb-10 sm:mb-14">
                    <div className="flex bg-slate-200 rounded-lg p-1 space-x-2">
                        <button
                            className={`px-6 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                                currency === '₹' ? 'bg-slate-800 text-white shadow' : 'text-slate-700 hover:bg-slate-300'
                            }`}
                            onClick={() => setCurrency('₹')}
                        >
                            Price in rupees
                        </button>
                        <button
                            className={`px-6 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                                currency === '$' ? 'bg-slate-800 text-white shadow' : 'text-slate-700 hover:bg-slate-300'
                            }`}
                            onClick={() => setCurrency('$')}
                        >
                            Price in dollars
                        </button>
                    </div>
                </div>

                {/* --- Main Content Grid --- */}
                <div className="flex flex-col lg:flex-row lg:space-x-12 items-start justify-center">
                    {/* --- Left Column: Description and Included Features --- */}
                    <div className="w-full lg:w-2/5 mb-10 lg:mb-0 lg:pr-8">
                        <p className="text-lg text-slate-700 leading-relaxed mb-6">
                            All plans give you access to the <span className="font-semibold text-cyan-500">full functionality of the platform</span>. Simply choose the number of monthly conversations you need.
                        </p>
                        <h3 className="text-xl font-semibold text-slate-800 mb-4">Included:</h3>
                        <ul className="space-y-3 text-slate-600">
                            {includedFeatures.map((feature, index) => (
                                <li key={index} className="flex items-center">
                                    <CheckCircle2 className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* --- Right Column: Pricing Options --- */}
                    <div className="w-full lg:w-3/5 bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-slate-200">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                            {plans[currency].map((plan, index) => (
                                <div key={index} className="flex items-center">
                                    <CircleDot className="h-5 w-5 text-slate-700 mr-3 flex-shrink-0" />
                                    <p className="text-slate-800 text-lg">
                                        <span className="font-bold">{plan.dialogs} dialogs</span> – <span className="font-semibold text-blue-500">{currency}{plan.price}/month</span>
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingPlans;

